import React, { useId } from 'react';
import { isWbrAgentActor } from 'src/constants/metricCallouts';

/**
 * The WBR Agent logo (supplied design: gradient circle + sparkles). The
 * source asset is a 28×44 box whose visual content is the r=14 circle centred
 * at (14,22); the viewBox is cropped to that circle (y 8–36) so the mark
 * scales proportionally. Sized in em so it always matches the surrounding
 * text height (the author line is body-s in the rail and thread alike).
 * The gradient id is unique per instance (useId): many logos render at once
 * in the rail, and duplicate SVG ids make url(#…) resolve to the first
 * instance in the document — invalid markup and fragile paint.
 */
const WbrAgentLogo: React.FC = () => {
  const gradientId = useId();
  return (
    <svg
      width="1.4em"
      height="1.4em"
      viewBox="0 8 28 28"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
      data-testid="wbr-agent-logo"
    >
      <circle cx="14" cy="22" r="14" fill={`url(#${gradientId})`} />
      <path
        d="M10.918 16.3763L9.27534 15.7182L8.61948 14.0728C8.57948 13.9757 8.41952 13.9757 8.37952 14.0728L7.72266 15.7182L6.08098 16.3763C6.03299 16.3963 6 16.4444 6 16.4964C6 16.5484 6.03199 16.5964 6.08098 16.6164L6.13097 16.6364L7.72266 17.2736L8.37952 18.919C8.39952 18.967 8.44751 19 8.4995 19C8.55149 19 8.59948 18.968 8.61948 18.919L9.27634 17.2736L10.919 16.6154C10.967 16.5954 11 16.5474 11 16.4954C11 16.4434 10.968 16.3953 10.919 16.3753L10.918 16.3763Z"
        fill="white"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M13.9991 15.0049C14.4083 15.0045 14.7765 15.2535 14.9285 15.6335L16.6291 19.885L20.8704 21.576C21.2502 21.7275 21.4996 22.095 21.5 22.504C21.5004 22.9129 21.2517 23.2809 20.8721 23.4331L16.6291 25.1339L14.9282 29.377C14.776 29.7566 14.408 30.0053 13.9991 30.0049C13.5901 30.0045 13.2226 29.7551 13.0711 29.3752L11.3801 25.1339L7.12861 23.4334C6.74864 23.2814 6.49962 22.9132 6.5 22.504C6.50038 22.0947 6.75008 21.727 7.13033 21.5757L11.3801 19.885L13.0708 15.6352C13.2221 15.255 13.5898 15.0053 13.9991 15.0049ZM14.0025 18.7037L13.0792 21.0245C12.9775 21.28 12.7752 21.4824 12.5197 21.584L10.1988 22.5074L12.5214 23.4364C12.7759 23.5382 12.9774 23.74 13.0789 23.9945L14.0025 26.311L14.9318 23.9928C15.0334 23.7393 15.2344 23.5383 15.4879 23.4367L17.8062 22.5074L15.4896 21.5838C15.2351 21.4823 15.0333 21.2807 14.9315 21.0263L14.0025 18.7037Z"
        fill="white"
      />
      <defs>
        <radialGradient
          id={gradientId}
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(30.4992 5.50085) rotate(135) scale(46.6667 59.7097)"
        >
          <stop stopColor="#B8E7FF" />
          <stop offset="0.3" stopColor="#0099FF" />
          <stop offset="0.45" stopColor="#5C7FFF" />
          <stop offset="0.6" stopColor="#8575FF" />
          <stop offset="0.8" stopColor="#962EFF" />
        </radialGradient>
      </defs>
    </svg>
  );
};

/**
 * Author display for callouts, comments, and replies: every author name is
 * bold; the WBR Agent is additionally prefixed with its logo so
 * agent-authored content stands out from human authors. Display-only:
 * author identity comparisons (sameActor / isMine) always run on the raw
 * author string.
 */
export const CalloutAuthor: React.FC<{ author: string }> = ({ author }) =>
  isWbrAgentActor(author) ? (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.35em',
        verticalAlign: 'bottom',
      }}
    >
      <WbrAgentLogo />
      <strong>{author}</strong>
    </span>
  ) : (
    <strong>{author}</strong>
  );
